import { useCallback, useEffect, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getCreditsInfo } from '../features/organizations/api';
import type { CreditsInfo } from '../features/organizations/types';
import { consumePurchaseMarker } from '../features/payments/purchaseMarker';

/**
 * ポイント購入後、Webhook による残高反映を待ってフルリロードなしに画面へ反映するフック。
 *
 * docs/requirements/credit-balance-live-refresh-plan.md 参照。
 *
 * 設計の要点:
 * - 判定 (fetch) と UI 更新 (invalidate) を分離する。
 *   - 判定用: `getCreditsInfo()` を能動 fetch (staleTime:0) し、その戻り値で
 *     `total_available >= baseline + expectedPoints` を評価する。
 *     (`invalidateQueries` はアクティブなクエリしか再取得しないため、
 *      `['organization','credits']` が非アクティブ/未作成のページでは判定が成立しない)
 * - UI 更新用: 各試行で残高/履歴系キーを invalidate し、マウント中の表示を最新化する。
 * - ポーリング本体はモジュールレベルのシングルトンで保持する。これにより
 *   React StrictMode の二重マウントや、購入直後のページ遷移をまたいでも
 *   ポーリングが途切れない (コンポーネントは phase を購読するだけ)。
 */

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 15000;

export type CreditRefreshPhase = 'idle' | 'polling' | 'reflected' | 'timeout';

/** CreditsInfo から合計利用可能ポイントを読む (Dashboard 表示と同じフォールバック順)。 */
function readAvailable(info: CreditsInfo | undefined): number {
  if (!info) return 0;
  return info.total_available ?? info.credits_balance ?? info.permanent_credits ?? 0;
}

/** 残高/履歴系クエリをまとめて無効化し、マウント中の表示を最新化する。 */
export function invalidateCreditViews(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['credits'] });
  queryClient.invalidateQueries({ queryKey: ['organization', 'credits'] });
  queryClient.invalidateQueries({ queryKey: ['organization', 'me'] });
  queryClient.invalidateQueries({ queryKey: ['organization', 'transactions'] });
}

/**
 * 判定用 fetchQuery で `['organization','credits']` は既に最新化済みのため、
 * それ以外のビュー (ヘッダー・プラン・取引履歴) だけを無効化する。
 * 同一エンドポイントへの二重リクエストを避ける。
 */
function invalidateOtherCreditViews(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['credits'] });
  queryClient.invalidateQueries({ queryKey: ['organization', 'me'] });
  queryClient.invalidateQueries({ queryKey: ['organization', 'transactions'] });
}

/**
 * 購入開始時点の基準残高を解決する。能動 fetch (staleTime:0) で最新化する。
 *
 * 失敗時に stale cache / 0 へフォールバックすると、既存残高が expectedPoints 以上ある
 * ユーザーでは Webhook 反映前の最初の fetch だけで `baseline + expectedPoints` を満たし、
 * 誤って reflected になる。そのため**信頼できる baseline が取れなければ null を返し**、
 * 呼び出し側は「閾値判定をしない固定ポーリング」(後述) に倒す。
 * (決済自体はブロックしない)
 *
 * @returns 最新残高。能動 fetch に失敗したら null (= baseline 不明)。
 */
export async function resolveBaselineBalance(queryClient: QueryClient): Promise<number | null> {
  try {
    const fetched = await queryClient.fetchQuery({
      queryKey: ['organization', 'credits'],
      queryFn: getCreditsInfo,
      staleTime: 0,
    });
    return readAvailable(fetched);
  } catch {
    return null;
  }
}

interface StartArgs {
  /** 購入開始時点の合計利用可能ポイント。null = baseline 不明 (閾値判定をしない固定ポーリング)。 */
  baselineBalance: number | null;
  expectedPoints: number;
  /** 同一購入の二重起動をはじくためのキー (paymentIntentId 等)。任意。 */
  key?: string;
}

// ──────────────────────────────────────────────────────────────
// モジュールレベルのポーリングコントローラ (シングルトン)
// ──────────────────────────────────────────────────────────────

let phase: CreditRefreshPhase = 'idle';
let timer: ReturnType<typeof setTimeout> | null = null;
let active = false;
let activeKey: string | null = null;
const listeners = new Set<(p: CreditRefreshPhase) => void>();

function setPhase(next: CreditRefreshPhase): void {
  phase = next;
  listeners.forEach((l) => l(next));
}

function stopPoll(): void {
  active = false;
  activeKey = null;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

function startPoll(queryClient: QueryClient, { baselineBalance, expectedPoints, key }: StartArgs): void {
  // 同一購入が既に走っているなら何もしない (StrictMode 二重マウント・経路 A/B 重複対策)。
  if (active && key && activeKey === key) return;

  // 別の購入なら作り直す (最新の購入を優先)。
  stopPoll();
  active = true;
  activeKey = key ?? null;
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  // baseline 不明 (null) のときは閾値判定をしない「固定ポーリング」。
  // UI だけ更新し続け、タイムアウト時に軽い注意表示 (timeout) に倒す。
  const target = baselineBalance === null ? null : baselineBalance + expectedPoints;
  setPhase('polling');

  const tick = async () => {
    if (!active) return;

    let available: number | null = null;
    try {
      // 判定用の能動 fetch。グローバル staleTime (5分) を無視して必ずネットワークへ
      // 行かせる (staleTime:0)。さもないと Webhook 反映前のキャッシュを読み続けて
      // 判定が永遠に成立しない。戻り値は同キーのキャッシュにも載るので、
      // マウント中の購読は自然に最新化される。
      const info = await queryClient.fetchQuery({
        queryKey: ['organization', 'credits'],
        queryFn: getCreditsInfo,
        staleTime: 0,
      });
      available = readAvailable(info);
    } catch {
      // fetch 失敗は一時的な可能性があるので、タイムアウトまでは試行を続ける。
    }

    if (!active) return;

    // UI 更新用 (ヘッダー残高・取引履歴など)。
    // organization/credits は上の fetchQuery で更新済みなので除外する。
    invalidateOtherCreditViews(queryClient);

    // baseline が信頼できるときのみ反映済み判定する (target!==null)。
    if (target !== null && available !== null && available >= target) {
      stopPoll();
      setPhase('reflected');
      return;
    }

    if (Date.now() >= deadline) {
      stopPoll();
      setPhase('timeout');
      return;
    }

    timer = setTimeout(tick, POLL_INTERVAL_MS);
  };

  // 初回は即時に 1 回試行する (Webhook が既に反映済みなら 1 回で完了)。
  tick();
}

export function usePollCreditRefreshAfterPurchase() {
  const queryClient = useQueryClient();
  const [localPhase, setLocalPhase] = useState<CreditRefreshPhase>(phase);

  // モジュールレベルの phase を購読する (ポーリング本体は再マウントで途切れない)。
  useEffect(() => {
    listeners.add(setLocalPhase);
    setLocalPhase(phase);
    return () => {
      listeners.delete(setLocalPhase);
    };
  }, []);

  const start = useCallback(
    (args: StartArgs) => {
      startPoll(queryClient, args);
    },
    [queryClient],
  );

  /**
   * sessionStorage マーカーがあれば消費してポーリングを起動する。
   * 経路 A (インライン成功・現在ページ) / 経路 B (リダイレクト復帰・Dashboard) で共用。
   * @returns 起動したら true。
   */
  const startFromMarker = useCallback((): boolean => {
    const marker = consumePurchaseMarker();
    if (!marker) return false;
    startPoll(queryClient, {
      baselineBalance: marker.baselineBalance,
      expectedPoints: marker.expectedPoints,
      key: marker.paymentIntentId ?? String(marker.ts),
    });
    return true;
  }, [queryClient]);

  /** timeout バナー等を閉じるための明示リセット。 */
  const reset = useCallback(() => {
    stopPoll();
    setPhase('idle');
  }, []);

  return { phase: localPhase, start, startFromMarker, reset };
}
